/** Work-log table used on Work Log, Project → Work Log and Member pages — port of the original logTable(). */
import { Link } from 'react-router';
import type { Member, Project, WorkLog } from '@/types/models';
import { fmtDate, fmtH } from '@/domain/format';
import { ActionsCell, IconButton, NoRecords } from '@/ui/kit';
import { useConfirm } from '@/ui/kit';
import { PermissionGate } from '@/auth/ProtectedRoute';
import { useAction } from '@/hooks/data';
import { useAppActions } from '../actions';

export function WorkLogTable({ logs, projects, members, hideProject, hideMember }: { logs: WorkLog[]; projects: Project[]; members: Member[]; hideProject?: boolean; hideMember?: boolean }) {
  const act = useAppActions();
  const confirm = useConfirm();
  const del = useAction((s, id: string) => s.workLogs.remove(id), 'Work log deleted successfully.');
  const mname = (id: string) => members.find((m) => m.id === id)?.name || '—';
  const pname = (id: string) => (id ? projects.find((p) => p.id === id)?.name || '—' : 'General');
  const cols = 4 + (hideMember ? 0 : 1) + (hideProject ? 0 : 1);
  return (
    <div className="tbl-wrap">
      <table>
        <thead>
          <tr>
            <th style={{ width: 110 }}>Date</th>
            {hideMember ? null : <th>Member</th>}
            {hideProject ? null : <th>Project</th>}
            <th className="num">Hours</th>
            <th>Work done</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {logs.length ? (
            logs.map((l) => (
              <tr key={l.id}>
                <td className="mono">{fmtDate(l.date)}</td>
                {hideMember ? null : (
                  <td>
                    <Link to={`/members/${encodeURIComponent(l.memberId)}`} style={{ textDecoration: 'none', fontWeight: 600 }}>
                      {mname(l.memberId)}
                    </Link>
                  </td>
                )}
                {hideProject ? null : (
                  <td>
                    {l.projectId ? (
                      <Link to={`/projects/${encodeURIComponent(l.projectId)}`} style={{ textDecoration: 'none' }}>
                        {pname(l.projectId)}
                      </Link>
                    ) : (
                      <span className="muted">General</span>
                    )}
                  </td>
                )}
                <td className="num">{fmtH(l.hours)}</td>
                <td>{l.work || <span className="muted">—</span>}</td>
                <td>
                  <PermissionGate perm="worklog:write" fallback={<span className="muted">—</span>}>
                    <ActionsCell>
                      <IconButton icon="edit" tip="Edit" onClick={() => act.workLog({ id: l.id })} />
                      <IconButton icon="del" tip="Delete" danger onClick={() => confirm(`Are you sure you want to delete the ${fmtDate(l.date)} entry for ${mname(l.memberId)} (${fmtH(l.hours)})?`, () => del.run(l.id))} />
                    </ActionsCell>
                  </PermissionGate>
                </td>
              </tr>
            ))
          ) : (
            <NoRecords cols={cols} />
          )}
        </tbody>
      </table>
    </div>
  );
}
